/**
 * `/invite/:token/accept` — post-auth invite acceptance.
 *
 * Phase 14 Stream C, repositioned by Tom F1
 * (`docs/audits/ux-log-tom-member-2026-05-01.md`). The unauthenticated
 * preview at `/invite/:token` is now the public-facing landing — this
 * page is reached AFTER Clerk auth completes, via
 * `consumePendingInviteRedirect()` in `App.tsx`. Flow:
 *
 *   1. `RequireAuth` guards the route, so by the time this component
 *      mounts the user is signed in.
 *   2. POST `/api/invites/:token/accept`. The endpoint is idempotent
 *      so a refresh after acceptance lands on the same team.
 *   3. On success, redirect to `/teams/:teamId` with a welcome flash.
 *
 * The flash uses sessionStorage so it survives the redirect; the team
 * page's first render reads it and shows a one-time toast.
 *
 * Defensive bounce: if for some reason this component renders without
 * an `accountId` (race during AuthProvider hydration, dev-shim
 * unwinding), we redirect to the preview page rather than self-
 * stashing — the preview is the canonical pre-auth surface post-F1.
 */
import { CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";
import type { paths } from "../api/schema.gen";
import { Logo } from "../components/Logo";
import { Button, Card, CardContent, PageLead } from "../components/ui";
import { useAuth } from "../lib/AuthContext";

type InviteAcceptResponse =
  paths["/api/invites/{token}/accept"]["post"]["responses"]["200"]["content"]["application/json"];
type AccountMe = paths["/api/account/me"]["get"]["responses"]["200"]["content"]["application/json"];

const FLASH_KEY = "roster.flash";
// #2386 — the accept's 409 `reason` when the chat was asked to add the login.
const TELEGRAM_HOLDER_ASKED = "telegram_holder_asked";
export const TELEGRAM_POLL_MS = 3000;
// The question's Connect token lives 15 minutes; after that the page stops
// waiting and says so.
export const TELEGRAM_WAIT_MS = 15 * 60 * 1000;
const TELEGRAM_EXPIRED_MESSAGE =
  "The question in Telegram has expired. Reload this page to ask again.";

/** The dict-shaped refusal (`error.detail`), if any. */
function refusalDetail(err: ApiError): Record<string, unknown> | null {
  if (!err.body || typeof err.body !== "object") return null;
  const errorBody = (err.body as { error?: unknown }).error;
  if (!errorBody || typeof errorBody !== "object") return null;
  const detail = (errorBody as { detail?: unknown }).detail;
  if (!detail || typeof detail !== "object") return null;
  return detail as Record<string, unknown>;
}

export function InviteAcceptPage() {
  const { token = "" } = useParams<{ token: string }>();
  const { accountId, loading, authHeader } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "accepting" }
    | { kind: "ok"; teamId: string; teamName: string }
    | { kind: "telegram"; message: string; handle: string }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  useEffect(() => {
    if (loading || !accountId || !token || state.kind !== "idle") return;
    setState({ kind: "accepting" });
    (async () => {
      try {
        const headers = await authHeader();
        const result = await apiFetch<InviteAcceptResponse>(
          `/api/invites/${encodeURIComponent(token)}/accept`,
          { method: "POST", headers },
        );
        sessionStorage.setItem(
          FLASH_KEY,
          JSON.stringify({
            kind: "invite_accepted",
            teamName: result.team_name,
          }),
        );
        setState({ kind: "ok", teamId: result.team_id, teamName: result.team_name });
        // brief beat for the visual confirmation, then redirect
        setTimeout(() => navigate(`/teams/${result.team_id}`, { replace: true }), 800);
      } catch (err) {
        const message = err instanceof ApiError ? err.detail : (err as Error).message;
        const detail = err instanceof ApiError ? refusalDetail(err) : null;
        if (detail && detail.reason === TELEGRAM_HOLDER_ASKED) {
          const handle = typeof detail.telegram_handle === "string" ? detail.telegram_handle : "";
          setState({ kind: "telegram", message, handle });
          return;
        }
        setState({ kind: "error", message });
      }
    })();
  }, [loading, accountId, token, state.kind, authHeader, navigate]);

  // #2386 — the row is held by this person's Telegram-only account and the
  // chat was asked "Add <email> as a login?". Once they tap Add there, the
  // same sign-in resolves to that account (a different id). A full load
  // then lets AuthProvider read it and this page accept again, which lands
  // on the idempotent "already a member" path.
  useEffect(() => {
    if (state.kind !== "telegram" || !accountId) return;
    let stopped = false;
    const tick = async () => {
      try {
        const headers = await authHeader();
        const me = await apiFetch<AccountMe>("/api/account/me", { headers });
        if (!stopped && me.id !== accountId) {
          stopped = true;
          window.location.assign(window.location.pathname);
        }
      } catch {
        // keep waiting; the next tick tries again
      }
    };
    const timer = window.setInterval(tick, TELEGRAM_POLL_MS);
    const deadline = window.setTimeout(() => {
      stopped = true;
      window.clearInterval(timer);
      setState({ kind: "error", message: TELEGRAM_EXPIRED_MESSAGE });
    }, TELEGRAM_WAIT_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.clearTimeout(deadline);
    };
  }, [state.kind, accountId, authHeader]);

  if (!loading && !accountId) {
    // Defensive fallback: route to the unauthenticated preview, which
    // is the canonical pre-sign-in surface post Tom F1. The preview
    // handles its own stash + Clerk redirect.
    return <Navigate to={`/invite/${encodeURIComponent(token)}`} replace />;
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-6 px-4 py-12 text-center">
      <Logo />
      <PageLead
        className="mb-0 items-center text-center"
        title={
          state.kind === "accepting"
            ? "Accepting your invite…"
            : state.kind === "ok"
              ? "Invite accepted"
              : state.kind === "telegram"
                ? "Confirm in Telegram"
                : state.kind === "error"
                  ? "We couldn't accept this invite"
                  : "Join your team"
        }
        subtitle={state.kind === "error" || state.kind === "telegram" ? state.message : undefined}
      />
      <Card className="w-full">
        <CardContent className="flex flex-col items-center gap-4 p-6">
          {state.kind === "ok" ? (
            <>
              <CheckCircle2 className="h-8 w-8 text-success" aria-hidden="true" />
              <p className="text-base font-semibold text-fg">Welcome to {state.teamName}</p>
              <p className="text-sm text-fg-subtle">Taking you to the team page…</p>
            </>
          ) : state.kind === "telegram" ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-accent" aria-hidden="true" />
              <p className="text-base font-semibold text-fg">Waiting for your tap in Telegram</p>
              <p className="text-sm text-fg-subtle">
                {state.handle
                  ? `We asked @${state.handle}. Open the Tarii chat on Telegram and tap Add. This page continues by itself.`
                  : "Open the Tarii chat on Telegram and tap Add. This page continues by itself."}
              </p>
              <p className="text-xs text-fg-subtle">
                Not your Telegram? Close this page and tell your lead.
              </p>
            </>
          ) : state.kind === "error" ? (
            <>
              <p className="text-base font-semibold text-fg">Couldn't accept invite</p>
              <p className="text-sm text-danger">{state.message}</p>
              <Button asChild variant="secondary" size="sm">
                <a href="/">Back to home</a>
              </Button>
            </>
          ) : (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-accent" aria-hidden="true" />
              <p className="text-base font-semibold text-fg">Accepting your invite…</p>
              <p className="text-sm text-fg-subtle">One moment while we add you to the team.</p>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
